import {
  createBusinessPartnerBankAccountRequestSchema,
  createBusinessPartnerTaxProfileRequestSchema,
  masterDataDateSchema,
  patchBusinessPartnerBankAccountRequestSchema,
  patchBusinessPartnerTaxProfileRequestSchema,
  type BusinessPartnerDetail,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { MasterDataListState } from '@/platform/features/master-data/master-data-list-state';
import {
  useAddBusinessPartnerBankAccount,
  useAddBusinessPartnerTaxProfile,
  useCurrencies,
  usePatchBusinessPartnerBankAccount,
  usePatchBusinessPartnerTaxProfile,
} from '@/platform/features/master-data/use-master-data';

const optionalSecret = z.union([z.literal(''), z.string().trim().min(1)]);
const bankSuccessorSchema = patchBusinessPartnerBankAccountRequestSchema.extend({
  effectiveFrom: masterDataDateSchema,
  iban: optionalSecret.optional(),
});
const taxSuccessorSchema = patchBusinessPartnerTaxProfileRequestSchema.extend({
  effectiveFrom: masterDataDateSchema,
  vatId: optionalSecret.optional(),
  taxId: optionalSecret.nullable().optional(),
});

function blankToNull(value: string | null): string | null {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function toastServerError(error: unknown, fallback: string) {
  toast.error(error instanceof ApiRequestError ? error.message : fallback);
}

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }
  return (
    <p role="alert" className="text-sm text-destructive">
      {message}
    </p>
  );
}

export function PartnerBankSection({
  partnerId,
  accounts,
  canWrite,
}: {
  partnerId: string;
  accounts: BusinessPartnerDetail['bankAccounts'];
  canWrite: boolean;
}) {
  const [successorId, setSuccessorId] = useState<string | null>(null);

  return (
    <section className="max-w-xl space-y-3">
      <h2 className="text-base font-semibold">Bank</h2>
      {accounts.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch keine Bankverbindung.</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {accounts.map((account) => (
            <li key={account.id} className="space-y-2">
              <p>
                {account.accountHolder}
                {account.iban ? (
                  <span className="ml-2 font-mono">{account.iban}</span>
                ) : (
                  <span className="ml-2 font-mono">•••• {account.ibanLast4}</span>
                )}
                {` · ${account.currencyCode}`}
                {account.isPrimary ? <Badge className="ml-2">Primär</Badge> : null}
              </p>
              {canWrite && account.validTo == null && successorId !== account.id ? (
                <Button type="button" variant="outline" onClick={() => setSuccessorId(account.id)}>
                  Fortschreiben
                </Button>
              ) : null}
              {successorId === account.id ? (
                <BankSuccessorForm
                  partnerId={partnerId}
                  account={account}
                  onDone={() => setSuccessorId(null)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canWrite && successorId == null ? <BankCreateForm partnerId={partnerId} /> : null}
    </section>
  );
}

export function PartnerTaxSection({
  partnerId,
  profiles,
  canWrite,
}: {
  partnerId: string;
  profiles: BusinessPartnerDetail['taxProfiles'];
  canWrite: boolean;
}) {
  const [successorId, setSuccessorId] = useState<string | null>(null);

  return (
    <section className="max-w-xl space-y-3">
      <h2 className="text-base font-semibold">Steuer</h2>
      {profiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch kein Steuerprofil.</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {profiles.map((tax) => (
            <li key={tax.id} className="space-y-2">
              <p>
                {tax.country}
                {tax.vatId ? (
                  <span className="ml-2 font-mono">{tax.vatId}</span>
                ) : tax.vatIdLast4 ? (
                  <span className="ml-2 font-mono">•••• {tax.vatIdLast4}</span>
                ) : (
                  <span className="ml-2 text-muted-foreground">hinterlegt</span>
                )}
                {tax.taxId ? <span className="ml-2 font-mono">{tax.taxId}</span> : null}
              </p>
              {canWrite && tax.validTo == null && successorId !== tax.id ? (
                <Button type="button" variant="outline" onClick={() => setSuccessorId(tax.id)}>
                  Fortschreiben
                </Button>
              ) : null}
              {successorId === tax.id ? (
                <TaxSuccessorForm
                  partnerId={partnerId}
                  tax={tax}
                  onDone={() => setSuccessorId(null)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canWrite && successorId == null ? <TaxCreateForm partnerId={partnerId} /> : null}
    </section>
  );
}

function CurrencySelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (code: string) => void;
}) {
  const currencies = useCurrencies();
  const active = currencies.data?.items.filter((item) => item.isActive) ?? [];

  return (
    <MasterDataListState
      pending={currencies.isPending}
      pendingLabel="Währungen werden geladen …"
      error={currencies.isError ? currencies.error : null}
      errorLabel="Die Währungen konnten nicht geladen werden."
      empty={!currencies.isPending && !currencies.isError && active.length === 0}
      emptyLabel="Keine aktive Währung."
    >
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {active.map((item) => (
            <SelectItem key={item.id} value={item.code}>
              {item.code}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </MasterDataListState>
  );
}

function BankCreateForm({ partnerId }: { partnerId: string }) {
  const copy = useVoiceCopy();
  const create = useAddBusinessPartnerBankAccount(partnerId);
  const currencies = useCurrencies();
  const active = currencies.data?.items.filter((item) => item.isActive) ?? [];
  const form = useForm({
    resolver: zodResolver(createBusinessPartnerBankAccountRequestSchema),
    defaultValues: {
      accountHolder: '',
      iban: '',
      bic: null,
      bankName: null,
      currencyCode: '',
      isPrimary: false,
      validFrom: '',
      validTo: null,
    },
  });
  const [currencyCode, setCurrencyCode] = useState('');
  const selectedCurrency = currencyCode !== '' ? currencyCode : (active[0]?.code ?? '');

  async function onValid(values: {
    accountHolder: string;
    iban: string;
    bic?: string | null;
    bankName?: string | null;
    currencyCode: string;
    isPrimary?: boolean;
    validFrom: string;
    validTo?: string | null;
  }) {
    try {
      await create.mutateAsync({
        ...values,
        currencyCode: selectedCurrency,
        isPrimary: values.isPrimary ?? false,
      });
      toast.success('Bankverbindung angelegt.');
      form.reset({ ...form.getValues(), iban: '', validFrom: '' });
    } catch (error) {
      toastServerError(error, 'Die Bankverbindung konnte nicht angelegt werden.');
    }
  }

  const errors = form.formState.errors;

  return (
    <form
      className="space-y-4"
      noValidate
      onSubmit={(event) => {
        form.setValue('currencyCode', selectedCurrency);
        void form.handleSubmit(onValid)(event);
      }}
    >
      <div className="space-y-2">
        <Label htmlFor="bank-holder">Kontoinhaber</Label>
        <Input id="bank-holder" {...form.register('accountHolder')} />
        <FieldError
          message={errors.accountHolder ? copy('platform.form.account-holder') : undefined}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="bank-iban">IBAN</Label>
        <Input id="bank-iban" autoComplete="off" {...form.register('iban')} />
        <FieldError message={errors.iban ? copy('platform.form.iban') : undefined} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="bank-bic">BIC</Label>
        <Input id="bank-bic" {...form.register('bic', { setValueAs: blankToNull })} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="bank-currency">Währung</Label>
        <CurrencySelect
          id="bank-currency"
          value={selectedCurrency}
          onChange={(code) => {
            setCurrencyCode(code);
            form.setValue('currencyCode', code, { shouldValidate: true });
          }}
        />
        <FieldError message={errors.currencyCode ? copy('platform.form.currency') : undefined} />
      </div>
      <div className="flex items-center gap-2">
        <input id="bank-primary" type="checkbox" {...form.register('isPrimary')} />
        <Label htmlFor="bank-primary">Primärverbindung</Label>
      </div>
      <div className="space-y-2">
        <Label htmlFor="bank-from">Gültig ab</Label>
        <Input id="bank-from" placeholder="JJJJ-MM-TT" {...form.register('validFrom')} />
        <FieldError message={errors.validFrom ? copy('platform.form.date') : undefined} />
      </div>
      <Button type="submit" disabled={create.isPending || active.length === 0}>
        Bankverbindung anlegen
      </Button>
    </form>
  );
}

function BankSuccessorForm({
  partnerId,
  account,
  onDone,
}: {
  partnerId: string;
  account: BusinessPartnerDetail['bankAccounts'][number];
  onDone: () => void;
}) {
  const copy = useVoiceCopy();
  const patch = usePatchBusinessPartnerBankAccount(partnerId);
  const [currencyCode, setCurrencyCode] = useState(account.currencyCode);
  const form = useForm({
    resolver: zodResolver(bankSuccessorSchema),
    defaultValues: {
      accountHolder: account.accountHolder,
      iban: '',
      bic: account.bic,
      bankName: account.bankName,
      currencyCode: account.currencyCode,
      isPrimary: account.isPrimary,
      effectiveFrom: '',
    },
  });

  async function onValid(values: {
    accountHolder?: string;
    iban?: string;
    bic?: string | null;
    bankName?: string | null;
    currencyCode?: string;
    isPrimary?: boolean;
    effectiveFrom: string;
  }) {
    const iban = values.iban?.trim();
    try {
      await patch.mutateAsync({
        bankId: account.id,
        body: {
          accountHolder: values.accountHolder,
          ...(iban ? { iban } : {}),
          bic: values.bic,
          bankName: values.bankName,
          currencyCode: values.currencyCode,
          isPrimary: values.isPrimary ?? account.isPrimary,
          effectiveFrom: values.effectiveFrom,
        },
      });
      toast.success('Bankverbindung fortgeschrieben.');
      onDone();
    } catch (error) {
      toastServerError(error, 'Die Bankverbindung konnte nicht fortgeschrieben werden.');
    }
  }

  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor={`bank-${account.id}-iban`}>Neue IBAN</Label>
        <Input id={`bank-${account.id}-iban`} autoComplete="off" {...form.register('iban')} />
        <FieldError message={form.formState.errors.iban ? 'Die IBAN ist ungültig.' : undefined} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`bank-${account.id}-currency`}>Währung</Label>
        <CurrencySelect
          id={`bank-${account.id}-currency`}
          value={currencyCode}
          onChange={(code) => {
            setCurrencyCode(code);
            form.setValue('currencyCode', code);
          }}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`bank-${account.id}-from`}>Gültig ab</Label>
        <Input
          id={`bank-${account.id}-from`}
          placeholder="JJJJ-MM-TT"
          {...form.register('effectiveFrom')}
        />
        <FieldError
          message={form.formState.errors.effectiveFrom ? copy('platform.form.date') : undefined}
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={patch.isPending}>
          Bankverbindung fortschreiben
        </Button>
        <Button type="button" variant="outline" onClick={onDone}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

function TaxCreateForm({ partnerId }: { partnerId: string }) {
  const copy = useVoiceCopy();
  const create = useAddBusinessPartnerTaxProfile(partnerId);
  const form = useForm({
    resolver: zodResolver(createBusinessPartnerTaxProfileRequestSchema),
    defaultValues: {
      country: 'DE',
      vatId: '',
      taxId: null,
      validFrom: '',
      validTo: null,
    },
  });

  async function onValid(values: {
    country: string;
    vatId: string;
    taxId?: string | null;
    validFrom: string;
    validTo?: string | null;
  }) {
    try {
      await create.mutateAsync(values);
      toast.success('Steuerprofil angelegt.');
      form.reset({ country: 'DE', vatId: '', taxId: null, validFrom: '', validTo: null });
    } catch (error) {
      toastServerError(error, 'Das Steuerprofil konnte nicht angelegt werden.');
    }
  }

  const errors = form.formState.errors;

  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="tax-country">Land</Label>
        <Input id="tax-country" maxLength={2} {...form.register('country')} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="tax-vat">Umsatzsteuer-Id</Label>
        <Input id="tax-vat" autoComplete="off" {...form.register('vatId')} />
        <FieldError message={errors.vatId ? copy('platform.form.vat-id') : undefined} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="tax-id">Steuernummer</Label>
        <Input
          id="tax-id"
          autoComplete="off"
          {...form.register('taxId', { setValueAs: blankToNull })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="tax-from">Gültig ab</Label>
        <Input id="tax-from" placeholder="JJJJ-MM-TT" {...form.register('validFrom')} />
        <FieldError message={errors.validFrom ? copy('platform.form.date') : undefined} />
      </div>
      <Button type="submit" disabled={create.isPending}>
        Steuerprofil anlegen
      </Button>
    </form>
  );
}

function TaxSuccessorForm({
  partnerId,
  tax,
  onDone,
}: {
  partnerId: string;
  tax: BusinessPartnerDetail['taxProfiles'][number];
  onDone: () => void;
}) {
  const copy = useVoiceCopy();
  const patch = usePatchBusinessPartnerTaxProfile(partnerId);
  const form = useForm({
    resolver: zodResolver(taxSuccessorSchema),
    defaultValues: {
      country: tax.country,
      vatId: '',
      taxId: '',
      effectiveFrom: '',
    },
  });

  async function onValid(values: {
    country?: string;
    vatId?: string;
    taxId?: string | null;
    effectiveFrom: string;
  }) {
    const vatId = values.vatId?.trim();
    const taxId = values.taxId?.trim();
    try {
      await patch.mutateAsync({
        taxId: tax.id,
        body: {
          country: values.country,
          ...(vatId ? { vatId } : {}),
          ...(taxId ? { taxId } : {}),
          effectiveFrom: values.effectiveFrom,
        },
      });
      toast.success('Steuerprofil fortgeschrieben.');
      onDone();
    } catch (error) {
      toastServerError(error, 'Das Steuerprofil konnte nicht fortgeschrieben werden.');
    }
  }

  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor={`tax-${tax.id}-vat`}>Neue Umsatzsteuer-Id</Label>
        <Input id={`tax-${tax.id}-vat`} autoComplete="off" {...form.register('vatId')} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`tax-${tax.id}-from`}>Gültig ab</Label>
        <Input
          id={`tax-${tax.id}-from`}
          placeholder="JJJJ-MM-TT"
          {...form.register('effectiveFrom')}
        />
        <FieldError
          message={form.formState.errors.effectiveFrom ? copy('platform.form.date') : undefined}
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={patch.isPending}>
          Steuerprofil fortschreiben
        </Button>
        <Button type="button" variant="outline" onClick={onDone}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}
